import { Button, Chip, DialogContent, DialogTitle, Divider, Modal, ModalClose, ModalDialog, ModalOverflow, Snackbar, Typography, Table } from "@mui/joy"
import { useEffect, useState } from "react";
import { useAuth } from "../../context/authContext";
import { Report, CheckCircle, Error, Info } from '@mui/icons-material';

import LoadingSpinnerComponent from "../LoadingSpinnerComponent";
import EmptyMessageComponent from "../EmptyMessageComponent";

const replykitNetworking = require('../../Networking/Reply Kit/ReplykitAPI');
const helper = require('../../Helpers/helpers');

export type AutomationLog = {
    id: string;
    automation_id: string;
    review_id: string;
    executed_at: Date;
    status: 'success' | 'error' | 'pending';
    metadata?: {
        store: string;
        rating: string;
        replyText: string;
        sentiment: string;
    };
}

function AutomationsLogPopup({ app, subscription, showAutomationLogsModal, onClose }: any) {
    const { token } = useAuth();

    const [error, setError] = useState("");
    const [showError, setShowError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [automationLogs, setAutomationLogs] = useState<AutomationLog[]>([]);

    useEffect(() => {
        if (subscription && (subscription.plan === 'AAA' || subscription.plan === 'U')) {
            getAutomationLogs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscription]);

    const getAutomationLogs = () => {
        setLoading(true);

        replykitNetworking.getAutomationExecutionsLogOnKit(app.id, token).then((results: any) => {
            setAutomationLogs(results);
            setLoading(false);
        }).catch((error: Error) => {
            setError("Failed to fetch automation logs.");
            setShowError(true);
            setLoading(false);
        });
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'success':
                return <CheckCircle color="success" sx={{ width: 12 }} />;
            case 'error':
                return <Error color="error" sx={{ width: 12 }} />;
            case 'pending':
                return <Info color="warning" sx={{ width: 12 }} />;
            default:
                return <Info sx={{ width: 12 }} />;
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'success':
                return 'success';
            case 'error':
                return 'danger';
            case 'pending':
                return 'warning';
            default:
                return 'neutral';
        }
    }

    const formatDate = (date: Date) => {
        return helper.timeSince(date);
    }

    return (
        <>
            {/* Error handling */}
            {showError &&
                <Snackbar
                    variant="soft"
                    color="danger"
                    open={showError}
                    onClose={() => setShowError(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    startDecorator={<Report />}
                    endDecorator={
                        <Button
                            onClick={() => setShowError(false)}
                            size="sm"
                            variant="soft"
                            color="danger"
                        >
                            Dismiss
                        </Button>
                    }
                >
                    {error}
                </Snackbar>
            }

            {/* Automation Logs modal */}
            <Modal
                open={showAutomationLogsModal}
                onClose={() => onClose()}>
                <ModalOverflow>
                    {loading ?
                        <LoadingSpinnerComponent />
                        :
                        <ModalDialog
                            aria-labelledby="automation-logs-title"
                            size="lg"
                            sx={{ maxWidth: 800, width: '90vw' }}>
                            <ModalClose />
                            <DialogTitle id="automation-logs-title">
                                Automation Execution Logs
                            </DialogTitle>
                            <Divider />
                            <DialogContent sx={{ padding: 0, maxHeight: '70vh', overflow: 'auto' }}>
                                {automationLogs.length === 0 ? (
                                    <EmptyMessageComponent
                                        title={'No automation logs found.'}
                                        description={'There are no automation execution logs available at the moment.'} />
                                ) : (
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '120px' }}>Id</th>
                                                <th style={{ width: '120px' }}>Review Id</th>
                                                <th style={{ width: '160px' }}>Execution Date</th>
                                                <th style={{ width: '120px' }}>Status</th>
                                                <th style={{ width: '120px' }}>Store</th>
                                                <th style={{ width: '80px' }}>Rating</th>
                                                <th style={{ width: '200px' }}>Reply Text</th>
                                                <th style={{ width: '100px' }}>Sentiment</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {automationLogs.map((log) => (
                                                <tr key={log.id}>
                                                    <td>
                                                        <Typography level="body-xs">
                                                            {log.id}
                                                        </Typography>
                                                    </td>
                                                    <td>
                                                        <Typography level="body-xs">
                                                            {log.review_id}
                                                        </Typography>
                                                    </td>
                                                    <td>
                                                        <Typography level="body-xs">
                                                            {formatDate(log.executed_at)}
                                                        </Typography>
                                                    </td>
                                                    <td>
                                                        <Chip
                                                            variant="soft"
                                                            color={getStatusColor(log.status) as any}
                                                            startDecorator={getStatusIcon(log.status)}
                                                            size="sm"
                                                        >
                                                            {log.status}
                                                        </Chip>
                                                    </td>
                                                    <td>
                                                        <Typography level="body-xs">
                                                            {log.metadata?.store || '-'}
                                                        </Typography>
                                                    </td>
                                                    <td>
                                                        <Typography level="body-xs">
                                                            {log.metadata?.rating ? `⭐ ${log.metadata.rating}` : '-'}
                                                        </Typography>
                                                    </td>
                                                    <td>
                                                        <Typography level="body-xs" sx={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {log.metadata?.replyText || '-'}
                                                        </Typography>
                                                    </td>
                                                    <td>
                                                        <Chip
                                                            variant="outlined"
                                                            color={log.metadata?.sentiment === 'good' ? 'success' : log.metadata?.sentiment === 'bad' ? 'danger' : 'neutral'}
                                                            size="sm"
                                                        >
                                                            {log.metadata?.sentiment || '-'}
                                                        </Chip>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </DialogContent>
                            <Divider />
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '16px 0 8px 0'
                            }}>
                                <Typography level="body-sm" color="neutral">
                                    Total logs: {automationLogs.length}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={getAutomationLogs}
                                    size="sm"
                                >
                                    Refresh
                                </Button>
                            </div>
                        </ModalDialog>
                    }
                </ModalOverflow>
            </Modal>
        </>
    );
}

export default AutomationsLogPopup;
